import { ReactElement, useMemo } from 'react'
import { useSlabBeamToAssembly } from '@hooks'
import { ThreeEvent } from '@react-three/fiber'
import { useElementCrossSectionAssignment } from '@editorHooks'
import { getSlabBeamCrossSectionFromAssembly } from 'src/utils/assemblies'
import PositionMemberMeshElement, {
  useSlabBeamMeshIsTransparent,
} from '../PositionMemberMeshElement'
import { BeamStepSizeAreaVisual } from '../SlabMemberMeasure'

type CrossSectionDimensions = { width: number; height: number }

interface Props2 {
  data: VerticalSlab[]
  selectedElement?: string
  highlightedElements?: string[]

  onClick?: (slabForVertical: VerticalSlab, event: ThreeEvent<MouseEvent>) => void
  hiddenIds?: string[]
  /**
   * When true, the beam mesh reports its surface guid for click/marquee selection (Results2 selects
   * slabs in surface space). Leave false where selection is keyed on the beam guid (legacy Results).
   */
  selectViaSurfaceGuid?: boolean
}

type SlabBeamGroupProps = {
  slabForVertical: VerticalSlab
  selectedElement?: string
  highlightedElements?: string[]
  crossSection?: CrossSectionDimensions
  onClick?: (slabForVertical: VerticalSlab, event: ThreeEvent<MouseEvent>) => void
  selectViaSurfaceGuid?: boolean
}

/** Dimensions of the slab beam cross section, or undefined when it cannot be resolved. */
const getCrossSectionDimensions = (cs: unknown): CrossSectionDimensions | undefined => {
  const shape = (cs as { shape?: { width?: number; height?: number } } | undefined)?.shape
  if (shape && typeof shape.width === 'number' && typeof shape.height === 'number') {
    return { width: shape.width, height: shape.height }
  }
  return undefined
}

const SlabBeamGroup = ({
  slabForVertical,
  selectedElement,
  highlightedElements,
  crossSection,
  onClick,
  selectViaSurfaceGuid = false,
}: SlabBeamGroupProps): ReactElement => {
  const beamMeshTransparent = useSlabBeamMeshIsTransparent(slabForVertical.beam.guid)

  return (
    <>
      <PositionMemberMeshElement
        key={slabForVertical.beam.guid}
        data={
          {
            member: {
              start: slabForVertical.beam.shape.start,
              end: slabForVertical.beam.shape.end,
              guid: slabForVertical.beam.guid,
              coordinate_system: slabForVertical.beam.coordinate_system,
            },
            guid: slabForVertical.beam.guid,
            position_type: 'HorizontalRip',
          } as PositionMember
        }
        height={crossSection?.height}
        width={crossSection?.width}
        // Slab beams are not selectable in their own right — a click selects the surface.
        selectionGuid={selectViaSurfaceGuid ? slabForVertical.guid : undefined}
        onClick={e => onClick?.(slabForVertical, e)}
        isSelected={selectedElement === slabForVertical.beam.guid}
        isHighlighted={highlightedElements?.includes(slabForVertical.beam.guid)}
      />
      {!beamMeshTransparent ? (
        <BeamStepSizeAreaVisual
          slabBeam={slabForVertical.beam}
          stepSize={slabForVertical.step_size}
        />
      ) : null}
    </>
  )
}

const SlabBeamsWithStepSize = ({
  data,
  selectedElement,
  onClick,
  highlightedElements,
  hiddenIds = [],
  selectViaSurfaceGuid = false,
}: Props2): ReactElement | null => {
  const slabBeamGuidToAssembly = useSlabBeamToAssembly()
  const elementCrossSectionAssignment = useElementCrossSectionAssignment()

  // Slab beams carry no direct cross section by default — it comes from the slab's assembly.
  // A direct assignment, if present, overrides it (mirrors the detail pane resolution).
  const crossSectionByBeamGuid = useMemo(() => {
    const assignedCsByGuid: Record<string, unknown> = {}
    for (const assignment of elementCrossSectionAssignment) {
      assignedCsByGuid[assignment.element_guid] = assignment.element_cs
    }

    const map: Record<string, CrossSectionDimensions> = {}
    for (const slabForVertical of data) {
      const beamGuid = slabForVertical.beam?.guid
      if (!beamGuid) continue

      const assigned = assignedCsByGuid[beamGuid]
      const assembly = slabBeamGuidToAssembly[beamGuid]

      let dimensions = getCrossSectionDimensions(assigned)
      if (!dimensions && assembly) {
        try {
          dimensions = getCrossSectionDimensions(getSlabBeamCrossSectionFromAssembly(assembly))
        } catch {
          dimensions = undefined
        }
      }

      if (dimensions) map[beamGuid] = dimensions
    }
    return map
  }, [data, slabBeamGuidToAssembly, elementCrossSectionAssignment])

  return (
    <>
      {data.map(slabForVertical => {
        return (
          <group
            key={slabForVertical.beam.guid}
            visible={!hiddenIds.includes(slabForVertical.beam.guid)}
          >
            <SlabBeamGroup
              slabForVertical={slabForVertical}
              selectedElement={selectedElement}
              highlightedElements={highlightedElements}
              crossSection={crossSectionByBeamGuid[slabForVertical.beam.guid]}
              onClick={onClick}
              selectViaSurfaceGuid={selectViaSurfaceGuid}
            />
          </group>
        )
      })}
    </>
  )
}

export { SlabBeamsWithStepSize }
